import { useState } from "react";
import "../styles/AdminUploadForm.css";

const CATEGORIES = [
  "Oil Painting", "Acrylic", "Watercolor", "Mixed Media",
  "Photography", "Digital Art", "Sculpture", "Drawing", "Pastel", "Charcoal",
];

function fileKind(file) {
  if (!file) return null;
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return null;
}

export default function AdminUploadForm({ onAdd, showToast }) {
  const [dest, setDest] = useState("gallery");
  const [preview, setPreview] = useState(null);
  const [fileObj, setFileObj] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", price: "", category: CATEGORIES[0],
    details: "", date: "", desc: "", role: "",
  });

  function handleFile(file) {
    const kind = fileKind(file);
    if (!kind) {
      showToast("Please choose an image or video file.");
      return;
    }
    setFileObj(file);
    setMediaType(kind);
    const r = new FileReader();
    r.onload = (e) => setPreview(e.target.result);
    r.readAsDataURL(file);
  }

  async function handleSubmit() {
    if (dest !== "gallery" && !form.title.trim()) {
      showToast("Please enter a title.");
      return;
    }
    if (!fileObj) {
      showToast("Please upload a photo or video.");
      return;
    }

    const id = Date.now();
    const title = form.title.trim() || "Untitled";
    let item = { id, title, mediaType };

    if (dest === "shop") {
      if (!form.price) { showToast("Please enter a price."); return; }
      item = { ...item, price: Number(form.price), category: form.category, details: form.details };
    } else if (dest === "classroom") {
      if (!form.date) { showToast("Please enter a session date."); return; }
      item = { ...item, date: form.date, desc: form.desc || form.details };
    } else if (dest === "feedback") {
      const initials = title.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
      item = { ...item, name: title, role: form.role || "", initials };
    }

    setBusy(true);
    await onAdd(dest, item, fileObj);
    setBusy(false);
    showToast(`Saved to ${dest}`);
    setForm({ title: "", price: "", category: CATEGORIES[0], details: "", date: "", desc: "", role: "" });
    setPreview(null);
    setFileObj(null);
    setMediaType(null);
  }

  function setField(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  const dests = [
    ["gallery",   "",  "Gallery"],
    ["shop",      "",  "Shop"],
    ["classroom", "",  "Classroom"],
    ["feedback",  "",  "Customer Feedback"],
  ];

  return (
    <div className="upload-form">
      <div className="upload-form-title">New Upload</div>
      <div className="form-group">
        <label>Destination</label>
        <div className="radio-group">
          {dests.map(([v, ico, label]) => (
            <div key={v} className={`radio-option${dest === v ? " selected" : ""}`} onClick={() => setDest(v)}>
              <div className="radio-option-label">{label}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="form-group">
        <label>Photo or Video</label>
        <div
          className={`file-drop${drag ? " dragover" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
        >
          <input type="file" accept="image/*,video/*" onChange={(e) => handleFile(e.target.files[0])} />
          <p>{preview ? <strong>File ready</strong> : <><strong>Click or drag</strong> a photo or video</>}</p>
        </div>
        {preview && mediaType === "video" && <video src={preview} className="img-preview" controls muted playsInline />}
        {preview && mediaType !== "video" && <img src={preview} alt="preview" className="img-preview" />}
      </div>
      {dest !== "gallery" && (
        <div className="form-row">
          <div className="form-group">
            <label>{dest === "feedback" ? "Customer Name *" : "Title *"}</label>
            <input className="form-control" value={form.title} onChange={(e) => setField("title", e.target.value)} />
          </div>
          {dest === "shop" && (
            <div className="form-group">
              <label>Price (TZS) *</label>
              <input className="form-control" type="number" value={form.price} onChange={(e) => setField("price", e.target.value)} />
            </div>
          )}
          {dest === "classroom" && (
            <div className="form-group">
              <label>Session Date *</label>
              <input className="form-control" type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} />
            </div>
          )}
          {dest === "feedback" && (
            <div className="form-group">
              <label>Their Role</label>
              <input className="form-control" value={form.role} onChange={(e) => setField("role", e.target.value)} />
            </div>
          )}
        </div>
      )}
      {dest === "gallery" && (
        <div className="form-group">
          <label>Title (optional)</label>
          <input className="form-control" value={form.title} onChange={(e) => setField("title", e.target.value)} />
        </div>
      )}
      {dest === "shop" && (
        <div className="form-group">
          <label>Category / Medium</label>
          <select className="form-control" value={form.category} onChange={(e) => setField("category", e.target.value)}>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
      )}
      {(dest === "shop" || dest === "classroom") && (
        <div className="form-group">
          <label>{dest === "shop" ? "Artwork Details" : "Session Description"}</label>
          <textarea className="form-control" rows={4} value={dest === "shop" ? form.details : form.desc} onChange={(e) => dest === "shop" ? setField("details", e.target.value) : setField("desc", e.target.value)} />
        </div>
      )}
      <button className="btn btn-ochre" onClick={handleSubmit} disabled={busy}>
        {busy ? "Saving…" : "Save to site"}
      </button>
    </div>
  );
}
